if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;

      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.handleVariantInputChange = this.handleVariantInputChange.bind(this);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.variantIdInput?.addEventListener('change', this.handleVariantInputChange);
      }

      connectedCallback() {
        if (!document.body.classList.contains('template-product')) return;

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
        this.syncVariantCartState();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.variantIdInput?.removeEventListener('change', this.handleVariantInputChange);
      }

      handleVariantInputChange() {
        if (!document.body.classList.contains('template-product')) return;
        window.requestAnimationFrame(() => this.syncVariantCartState());
      }

      handleVariantChange({ data }) {
        if (!document.body.classList.contains('template-product')) return;
        if (data?.sectionId && String(data.sectionId) !== String(this.dataset.sectionId)) return;
        this.syncVariantCartState();
      }

      getCurrentVariantId() {
        const variantId = Number.parseInt(this.variantIdInput?.value || '', 10);
        return Number.isFinite(variantId) ? variantId : null;
      }

      isSoldOutOrUnavailableState() {
        if (!this.submitButton || !this.submitButtonText) return false;

        const buttonText = this.submitButtonText.textContent?.trim().toLowerCase() || '';
        const soldOutText = (window.variantStrings?.soldOut || '').trim().toLowerCase();
        const unavailableText = (window.variantStrings?.unavailable || '').trim().toLowerCase();
        const isUnavailableText = buttonText === soldOutText || buttonText === unavailableText;

        return this.submitButton.hasAttribute('disabled') && isUnavailableText;
      }

      applyVariantCartState(isInCart) {
        if (!this.submitButton || !this.submitButtonText) return;

        const isUnavailable = this.isSoldOutOrUnavailableState();

        if (isInCart) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('is-in-cart', 'pointer-events-none');
          this.submitButtonText.textContent = 'IN CART';
          return;
        }

        this.submitButton.classList.remove('is-in-cart', 'pointer-events-none');
        if (isUnavailable) return;

        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.textContent = window.variantStrings.addToCart;
      }

      syncVariantCartState() {
        if (!document.body.classList.contains('template-product')) return;

        const variantId = this.getCurrentVariantId();
        if (!variantId) {
          this.applyVariantCartState(false);
          return;
        }

        fetch(`${window.routes.cart_url}.js?t=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (!cart || !Array.isArray(cart.items)) return;
            const inCart = cart.items.some(
              (item) => Number(item.variant_id) === variantId && Number(item.quantity) > 0
            );
            this.applyVariantCartState(inCart);
            this.updateMobileCartBadge(cart.item_count, cart.total_price);
          })
          .catch(() => {});
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const isProductTemplate = document.body.classList.contains('template-product');
        const defaultButtonText = this.submitButtonText?.textContent?.trim() || window.variantStrings.addToCart;
        let cartAddSucceeded = false;

        if (isProductTemplate && this.submitButtonText) {
          this.submitButtonText.textContent = 'ADDING...';
          this.submitButton.classList.remove('is-in-cart', 'pointer-events-none');
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              if (this.submitButtonText) this.submitButtonText.textContent = defaultButtonText;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              cartAddSucceeded = true;
              if (isProductTemplate && this.submitButtonText) {
                this.updateMobileCartBadge(this.getCurrentMobileCartCount() + 1);
                this.applyVariantCartState(true);
                this.syncVariantCartState();
                return;
              }
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            cartAddSucceeded = true;
            if (isProductTemplate && this.submitButtonText) {
              this.updateMobileCartBadge(this.getCurrentMobileCartCount() + 1);
              this.applyVariantCartState(true);
              this.syncVariantCartState();
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            if (cartAddSucceeded && isProductTemplate) {
              this.applyVariantCartState(true);
            }

            if (!this.error && !this.submitButton.classList.contains('is-in-cart')) {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.removeAttribute('disabled');
            }
            this.querySelector('.loading__spinner').classList.add('hidden');
            if (!cartAddSucceeded && this.submitButtonText) {
              this.submitButtonText.textContent = defaultButtonText;
              this.submitButton.classList.remove('is-in-cart', 'pointer-events-none');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.submitButton.classList.remove('is-in-cart', 'pointer-events-none');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.submitButton.classList.remove('is-in-cart', 'pointer-events-none');
        }
      }

      syncMobileCartCount() {
        fetch(`${window.routes.cart_url}.js?t=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (!cart) return;
            this.updateMobileCartBadge(cart.item_count, cart.total_price);
          })
          .catch(() => {});
      }

      updateMobileCartBadge(itemCount, totalPriceCents = null) {
        const itemLabel = itemCount === 1 ? 'item' : 'items';
        const formattedTotal =
          typeof totalPriceCents === 'number' ? this.formatCartTotal(totalPriceCents) : null;

        document.querySelectorAll('.header__mobile-cart-count').forEach((count) => {
          count.textContent = itemCount;
        });
        document.querySelectorAll('.header__mobile-cart').forEach((badge) => {
          badge.classList.toggle('is-hidden', itemCount < 1);
          badge.setAttribute('aria-label', `Cart: ${itemCount}`);
        });

        document.querySelectorAll('#cart-icon-bubble').forEach((badge) => {
          badge.classList.toggle('is-hidden', itemCount < 1);
          badge.setAttribute('aria-label', `Cart: ${itemCount}`);
          const cartBox = badge.querySelector('.header__cart-box');
          if (cartBox) cartBox.setAttribute('data-item-count', itemCount);
          const cartCount = badge.querySelector('.header__cart-count');
          if (cartCount) cartCount.textContent = `${itemCount} ${itemLabel}`;
          const cartTotal = badge.querySelector('.header__cart-total');
          if (cartTotal && formattedTotal) cartTotal.textContent = formattedTotal;
          const hiddenSummary = badge.querySelector('.visually-hidden');
          if (hiddenSummary) {
            const totalText = formattedTotal || cartTotal?.textContent?.trim() || '$0.00';
            hiddenSummary.textContent = `Cart: ${itemCount} ${itemLabel}, ${totalText}`;
          }
        });
      }

      formatCartTotal(totalPriceCents) {
        if (window.Shopify?.formatMoney) {
          const format = window.Shopify.money_format || '${{amount}}';
          try {
            const money = window.Shopify.formatMoney(totalPriceCents, format);
            return typeof money === 'string' ? money.replace(/<[^>]*>/g, '').trim() : `${money}`;
          } catch (_) {}
        }

        const activeCurrency = window.Shopify?.currency?.active;
        if (activeCurrency) {
          try {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency: activeCurrency }).format(
              totalPriceCents / 100
            );
          } catch (_) {}
        }

        const fallbackCurrent = document.querySelector('#cart-icon-bubble .header__cart-total')?.textContent?.trim() || '$0.00';
        const fallbackSymbol = fallbackCurrent.match(/[^\d\s.,-]+/)?.[0] || '$';
        return `${fallbackSymbol}${(totalPriceCents / 100).toFixed(2)}`;
      }

      getCurrentMobileCartCount() {
        const cartCount = document.querySelector('.header__mobile-cart-count');
        return Number.parseInt(cartCount?.textContent || '0', 10) || 0;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
